import React, { useEffect, useRef, useState } from "react";
import clsx from "clsx";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";

import * as Button from "components/Button";
import ChevronSVG from "modules/theme/icons/chevrons/right-chevron.svg";
import CrossSVG from "modules/theme/icons/general/cross.svg";
import { DropdownItemProps, DropdownMultipleProps } from "../types";
import { DropdownMatchesType } from "../constants";
import { isIOS, isMobile } from "modules/platform/context";
import { selectedId, useDropdownPosition } from "../hook";
import muiStylesOverideMultiple from "./muiStylesOveride";
import LoadingSVG from "modules/theme/icons/loading/button-loading.svg";

import * as styles from "../styles.module.scss";

const Multiple: React.FC<DropdownMultipleProps> = ({
  activeItems,
  appendTo,
  dropdownSelector = DropdownMatchesType.ID,
  empty,
  forwardedContainerRef,
  items,
  label,
  multiple = true,
  name,
  setActiveItems,
  type,
}) => {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [isEmpty, setIsEmpty] = useState(empty ? true : false);
  const buttonRef = useRef(null);
  const containerWidth = useDropdownPosition(forwardedContainerRef);
  const isLoading = items !== undefined && items.length === 0;
  const open = Boolean(anchorEl);

  const className = clsx(
    styles.dropdown,
    isLoading && styles.loading,
    isEmpty && styles.empty,
    open && styles.isOpen,
    activeItems.length > 1 && styles.multiSelectActive,
    activeItems.length === 1 && styles.singleSelectActive
  );
  const muiStyleOveride = muiStylesOverideMultiple();

  const labelName = () => {
    if (activeItems.length > 1) {
      return activeItems.length.toString();
    } else if (activeItems.length === 1 && !activeItems.includes(label)) {
      const single = items?.filter((i) => i[dropdownSelector] === activeItems[0]);
      return single && single[0]?.title;
    }
    return name;
  };

  const handleItemClick = (item: DropdownItemProps) => {
    if (!multiple) {
      setActiveItems("", type);
    }
    setActiveItems(selectedId(item, dropdownSelector), type);
  };

  useEffect(() => {
    if (items === undefined) {
      setIsEmpty(true);
    } else {
      setIsEmpty(false);
    }
  }, [items]);

  useEffect(() => {
    const resizeListener = () => setAnchorEl(null);
    if (anchorEl && !isMobile) {
      window.addEventListener("resize", resizeListener);
    }
    return () => {
      window.removeEventListener("resize", resizeListener);
    };
  }, [anchorEl, isMobile]);

  useEffect(() => {
    const scroll = () => setAnchorEl(null);
    if (anchorEl && isIOS) {
      window.removeEventListener("scroll", scroll);
      window.addEventListener("scroll", scroll, {
        passive: false,
      });
    }
    return () => {
      window.removeEventListener("scroll", scroll);
    };
  }, [anchorEl]);

  return (
    <div className={className}>
      <Button.Generic
        disabled={isLoading || isEmpty}
        forwardedRef={buttonRef}
        label={labelName()}
        onClick={() => setAnchorEl(buttonRef.current)}
      >
        {isLoading ? <LoadingSVG /> : <ChevronSVG />}
      </Button.Generic>
      <Select
        style={{ display: "none" }}
        className={`${muiStyleOveride.select}`}
        disabled={isLoading || isEmpty}
        IconComponent={ChevronSVG}
        MenuProps={{
          style: {
            width: `${containerWidth}px`,
            zIndex: "999",
          },
          open: open,
          anchorEl: anchorEl,
          getContentAnchorEl: null,
          anchorOrigin: { vertical: "bottom", horizontal: "left" },
          transformOrigin: { vertical: "top", horizontal: "left" },
          classes: { paper: muiStyleOveride.dropdown },
          variant: "menu",
        }}
        multiple={multiple}
        onClose={() => setAnchorEl(null)}
        value={activeItems.length > 0 ? activeItems : [label]}
      >
        <MenuItem className={muiStyleOveride.menuItem} onClick={() => setActiveItems("", type)} value={label}>
          {label}
        </MenuItem>
        {items &&
          items.map((item, key) => {
            return (
              <MenuItem
                className={muiStyleOveride.menuItem}
                key={selectedId(item, dropdownSelector) + key}
                onClick={() => handleItemClick(item)}
                value={item[dropdownSelector]}
              >
                {appendTo ? item.title + appendTo : item.title}
                <CrossSVG />
              </MenuItem>
            );
          })}
      </Select>
    </div>
  );
};

export default Multiple;
